/**
 * lib/data/checkout-countries.ts
 *
 * The country list the checkout modal offers, and the EU VAT rate that goes with
 * each EU member state. Verbatim from the approved design source
 * `Team Inbox/checkout-mockup-APPROVED-2026-08-27.html` (its inline `EU` + `ROW`
 * arrays) — every one of the ~195 countries, EU first as its own optgroup.
 *
 * WHAT THIS IS NOT: it is not a tax authority. The rate here drives the DISPLAY
 * line only ("includes €83.13 VAT (21%, Netherlands)"). The amount actually
 * charged is decided server-side from the buyer's country, never from anything
 * the browser sends. Same discipline as the one-time promo route: the browser
 * states, the server decides.
 */

export interface EuCountry {
  /** ISO 3166-1 alpha-2. */
  code: string;
  name: string;
  /** Standard VAT rate, percent. */
  vat: number;
}

/** EU-27, with the standard VAT rate applied to electronically supplied services. */
export const EU_COUNTRIES: EuCountry[] = [
  { code: 'AT', name: 'Austria', vat: 20 },
  { code: 'BE', name: 'Belgium', vat: 21 },
  { code: 'BG', name: 'Bulgaria', vat: 20 },
  { code: 'HR', name: 'Croatia', vat: 25 },
  { code: 'CY', name: 'Cyprus', vat: 19 },
  { code: 'CZ', name: 'Czechia', vat: 21 },
  { code: 'DK', name: 'Denmark', vat: 25 },
  { code: 'EE', name: 'Estonia', vat: 24 },
  { code: 'FI', name: 'Finland', vat: 25.5 },
  { code: 'FR', name: 'France', vat: 20 },
  { code: 'DE', name: 'Germany', vat: 19 },
  { code: 'GR', name: 'Greece', vat: 24 },
  { code: 'HU', name: 'Hungary', vat: 27 },
  { code: 'IE', name: 'Ireland', vat: 23 },
  { code: 'IT', name: 'Italy', vat: 22 },
  { code: 'LV', name: 'Latvia', vat: 21 },
  { code: 'LT', name: 'Lithuania', vat: 21 },
  { code: 'LU', name: 'Luxembourg', vat: 17 },
  { code: 'MT', name: 'Malta', vat: 18 },
  { code: 'NL', name: 'Netherlands', vat: 21 },
  { code: 'PL', name: 'Poland', vat: 23 },
  { code: 'PT', name: 'Portugal', vat: 23 },
  { code: 'RO', name: 'Romania', vat: 21 },
  { code: 'SK', name: 'Slovakia', vat: 23 },
  { code: 'SI', name: 'Slovenia', vat: 22 },
  { code: 'ES', name: 'Spain', vat: 21 },
  { code: 'SE', name: 'Sweden', vat: 25 },
];

/**
 * Everywhere else. No EU VAT is due; the modal says so in plain words.
 *
 * ⚠️ EACH ENTRY CARRIES ITS REAL ISO 3166-1 ALPHA-2 CODE, and that is
 * load-bearing. Until 2026-08-27 this was a list of NAMES and the modal sent
 * every rest-of-world buyer the placeholder code `--`. That was harmless only
 * while the membership Pay button was a link to /join. It is not harmless now:
 * the mint route validates the country against /^[A-Z]{2}$/ before resolving
 * the tax region (pf-platform lib/commerce/tax-region.ts) and REFUSES anything
 * else rather than guess a region — so `--` would have turned "Continue" into
 * "Please select your country." for every buyer outside the EU. The code is
 * also what is written onto the Stripe Customer's address, which is the field a
 * tax audit reads.
 *
 * None of these are in the EU VAT territory, so all of them resolve to `row`.
 * Iceland, Liechtenstein, Norway, Switzerland, the UK, Monaco, San Marino and
 * the Vatican are here deliberately: EEA or European, but outside EU VAT.
 */
export const ROW_COUNTRIES: { code: string; name: string }[] = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: 'Côte d’Ivoire' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CD', name: 'Democratic Republic of the Congo' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IL', name: 'Israel' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'XK', name: 'Kosovo' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];

export interface SelectedCountry {
  /** ISO code for EU members; '--' for the rest of the world. */
  code: string;
  name: string;
  /** 0 outside the EU. */
  vat: number;
}

export const DEFAULT_COUNTRY: SelectedCountry = { code: 'NL', name: 'Netherlands', vat: 21 };

/** Postal-code placeholder, so the field reads as the buyer's own format. */
export function postalPlaceholder(country: SelectedCountry): string {
  if (country.code === 'NL') return '1017 AB';
  if (country.name === 'United States') return '10001';
  if (country.name === 'United Kingdom') return 'SW1A 1AA';
  return 'Postal code';
}
